import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FileUp, ChevronRight, AlertTriangle, CheckCircle2, Info, Search } from 'lucide-react';
import { Button } from '../components/ui/button';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';

import { BACKEND_URL } from '../config';

export default function JpkAnalysisPage() {
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [result, setResult] = useState('');
  const [loading, setLoading] = useState(false);
  const [fileInfo, setFileInfo] = useState(null);

  const handleFileSelect = (e) => {
    const f = e.target.files[0];
    if (f) {
      setFile(f);
      setFileInfo({ name: f.name, size: (f.size / 1024).toFixed(1) + ' KB' });
      setResult('');
    }
  };

  const handleAnalyze = async () => {
    if (!file) { toast.error('Wybierz plik JPK'); return; }
    setLoading(true);
    setResult('');
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${BACKEND_URL}/api/jpk/analyze`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
        timeout: 120000,
      });
      setResult(res.data.analysis);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Błąd analizy JPK');
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-amber-50 dark:from-slate-900 dark:to-slate-800">
      <div className="border-b bg-white/80 dark:bg-slate-900/80 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard')} data-testid="back-to-dashboard">
            <ChevronRight className="w-4 h-4 rotate-180" /> Powrót
          </Button>
          <h1 className="text-lg font-bold text-slate-800 dark:text-white flex items-center gap-2">
            <Search className="w-5 h-5 text-amber-600" /> Analiza JPK_VAT
          </h1>
        </div>
      </div>

      <div className="max-w-6xl mx-auto p-4 lg:p-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Upload Panel */}
          <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-5">
            <h2 className="font-bold text-slate-800 dark:text-white mb-2">Plik JPK</h2>
            <p className="text-xs text-slate-500 dark:text-slate-400 mb-4">
              Prześlij plik JPK_V7M (XML) aby sprawdzić jego poprawność przed wysyłką do US.
            </p>

            {/* Drop zone */}
            <div
              data-testid="jpk-upload-area"
              onClick={() => fileRef.current?.click()}
              className="border-2 border-dashed border-slate-300 dark:border-slate-600 rounded-xl p-8 text-center cursor-pointer hover:border-amber-400 hover:bg-amber-50/30 dark:hover:bg-amber-900/10 transition-all"
            >
              <FileUp className="w-10 h-10 text-amber-500 mx-auto mb-3" />
              <p className="text-sm font-medium text-slate-700 dark:text-slate-300">
                {file ? file.name : 'Kliknij aby wybrać plik'}
              </p>
              {fileInfo && (
                <p className="text-xs text-slate-400 mt-1">{fileInfo.size}</p>
              )}
              <p className="text-xs text-slate-400 mt-2">XML, CSV, TXT</p>
            </div>
            <input ref={fileRef} type="file" className="hidden" accept=".xml,.csv,.txt"
              onChange={handleFileSelect} />

            {/* Info cards */}
            <div className="mt-4 space-y-2">
              <div className="flex items-start gap-2 p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg">
                <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
                <p className="text-xs text-amber-700 dark:text-amber-400">
                  AI sprawdzi: spójność kwot, poprawność NIP, stawki VAT, brakujące pola
                </p>
              </div>
              <div className="flex items-start gap-2 p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
                <CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                <p className="text-xs text-green-700 dark:text-green-400">
                  Plik nie jest wysyłany do US - analizowany jest lokalnie przez AI
                </p>
              </div>
              <div className="flex items-start gap-2 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                <Info className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
                <p className="text-xs text-blue-700 dark:text-blue-400">
                  Obsługiwane: JPK_V7M, JPK_V7K, JPK_FA, JPK_WB
                </p>
              </div>
            </div>

            <Button
              data-testid="analyze-jpk-btn"
              onClick={handleAnalyze}
              disabled={!file || loading}
              className="w-full mt-5 bg-amber-600 hover:bg-amber-700 text-white h-11"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Analizuję plik...
                </span>
              ) : (
                <span className="flex items-center gap-2"><Search className="w-4 h-4" /> Analizuj JPK</span>
              )}
            </Button>
          </div>

          {/* Results */}
          <div className="lg:col-span-2 bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-5">
            <h2 className="font-bold text-slate-800 dark:text-white mb-3">Wyniki analizy</h2>
            {result ? (
              <ScrollArea className="h-[600px]">
                <div
                  data-testid="jpk-analysis-result"
                  className="prose prose-sm prose-slate dark:prose-invert max-w-none"
                  dangerouslySetInnerHTML={{ __html: result.replace(/\n/g, '<br/>') }}
                />
              </ScrollArea>
            ) : (
              <div className="h-[500px] flex items-center justify-center text-slate-400">
                <div className="text-center">
                  <Search className="w-16 h-16 mx-auto mb-4 opacity-20" />
                  <p className="text-sm">Prześlij plik JPK aby rozpocząć analizę</p>
                  <p className="text-xs mt-2">Otrzymasz raport z listą problemów i rekomendacjami</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
